<script setup lang="ts" generic="T extends string | number">
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';
import './equipment-ui.css';

export interface SelectOption<V> {
  value: V;
  label: string;
  // Une seconde ligne sous le libellé : le type d'un appareil, le commerce d'un site.
  description?: string;
  icon?: string;
}

const props = withDefaults(
  defineProps<{
    options: SelectOption<T>[];
    placeholder?: string;
    disabled?: boolean;
    // Au-delà de ce nombre d'options, une recherche apparaît ; 0 la désactive.
    searchAbove?: number;
    ariaLabel?: string;
    icon?: string;
  }>(),
  {
    placeholder: '',
    disabled: false,
    searchAbove: 7,
    ariaLabel: undefined,
    icon: undefined,
  },
);
const model = defineModel<T>({ required: true });

const uid = useId();
const open = ref(false);
const query = ref('');
const active = ref(0);
const trigger = ref<HTMLButtonElement | null>(null);
const panel = ref<HTMLElement | null>(null);
const searchBox = ref<HTMLInputElement | null>(null);
const place = ref({ top: 0, left: 0, width: 0, maxHeight: 288, up: false });

const selected = computed(() =>
  props.options.find((option) => option.value === model.value),
);
const searchable = computed(
  () => props.searchAbove > 0 && props.options.length > props.searchAbove,
);
const shown = computed(() => {
  const needle = query.value.trim().toLowerCase();
  return needle
    ? props.options.filter((option) =>
        `${option.label} ${option.description ?? ''}`
          .toLowerCase()
          .includes(needle),
      )
    : props.options;
});

const optionId = (index: number) => `${uid}-option-${index}`;

// Le menu est posé en fixe sur la page : il ne doit pas être coupé par une modale ni par un tableau qui défile.
const GAP = 6;
const MAX_HEIGHT = 288;
const measure = () => {
  const box = trigger.value?.getBoundingClientRect();
  if (!box) return;
  const below = window.innerHeight - box.bottom - GAP - 12;
  const above = box.top - GAP - 12;
  const up = below < 180 && above > below;
  const room = Math.max(120, up ? above : below);
  place.value = {
    top: up ? box.top - GAP : box.bottom + GAP,
    left: box.left,
    width: box.width,
    maxHeight: Math.min(MAX_HEIGHT, room),
    up,
  };
};

const scrollToActive = () =>
  nextTick(() =>
    panel.value
      ?.querySelector<HTMLElement>(`[id="${optionId(active.value)}"]`)
      ?.scrollIntoView({ block: 'nearest' }),
  );

const close = () => {
  open.value = false;
  query.value = '';
};

const show = async () => {
  if (props.disabled || open.value) return;
  measure();
  query.value = '';
  const current = props.options.findIndex((o) => o.value === model.value);
  active.value = Math.max(0, current);
  open.value = true;
  await nextTick();
  if (searchable.value) searchBox.value?.focus();
  scrollToActive();
};

const choose = (option: SelectOption<T>) => {
  model.value = option.value;
  close();
  trigger.value?.focus();
};

const move = (by: number) => {
  if (!shown.value.length) return;
  active.value = (active.value + by + shown.value.length) % shown.value.length;
  scrollToActive();
};

const onKey = (event: KeyboardEvent) => {
  if (!open.value) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      void show();
    }
    return;
  }
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      move(1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      move(-1);
      break;
    case 'Home':
      event.preventDefault();
      active.value = 0;
      scrollToActive();
      break;
    case 'End':
      event.preventDefault();
      active.value = Math.max(0, shown.value.length - 1);
      scrollToActive();
      break;
    case 'Enter': {
      event.preventDefault();
      const option = shown.value[active.value];
      if (option) choose(option);
      break;
    }
    case 'Escape':
      // Sans stop, la touche fermerait aussi la modale qui contient ce menu.
      event.stopPropagation();
      close();
      trigger.value?.focus();
      break;
    case 'Tab':
      close();
      break;
  }
};

watch(query, () => (active.value = 0));

const onPointerDown = (event: MouseEvent) => {
  const target = event.target as Node;
  if (trigger.value?.contains(target) || panel.value?.contains(target)) return;
  close();
};
// Un défilement ou un redimensionnement déplace le bouton : le menu le suit au lieu de rester accroché au vide.
const follow = () => {
  if (open.value) measure();
};

const listen = (on: boolean) => {
  if (on) {
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
  } else {
    document.removeEventListener('mousedown', onPointerDown);
    window.removeEventListener('resize', follow);
    window.removeEventListener('scroll', follow, true);
  }
};
watch(open, listen);
onBeforeUnmount(() => listen(false));
</script>

<template>
  <div class="eqsel" :class="{ 'eqsel--open': open }">
    <button
      ref="trigger"
      type="button"
      class="eqsel__trigger"
      :class="{ 'eqsel__trigger--placeholder': !selected }"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="open"
      :aria-controls="`${uid}-list`"
      :aria-label="ariaLabel"
      :aria-activedescendant="open ? optionId(active) : undefined"
      :disabled="disabled"
      @click="open ? close() : show()"
      @keydown="onKey"
    >
      <i
        v-if="selected?.icon ?? icon"
        class="pi eqsel__lead"
        :class="selected?.icon ?? icon"
      />
      <span class="eqsel__value">{{ selected?.label ?? placeholder }}</span>
      <i class="pi pi-chevron-down eqsel__chevron" />
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="panel"
        class="eqsel__panel"
        :class="{ 'eqsel__panel--up': place.up }"
        :style="{
          top: `${place.top}px`,
          left: `${place.left}px`,
          minWidth: `${place.width}px`,
          maxHeight: `${place.maxHeight}px`,
        }"
      >
        <div v-if="searchable" class="eqsel__search">
          <i class="pi pi-search" />
          <input
            ref="searchBox"
            v-model="query"
            type="text"
            autocomplete="off"
            :aria-controls="`${uid}-list`"
            @keydown="onKey"
          />
        </div>
        <ul :id="`${uid}-list`" class="eqsel__list" role="listbox">
          <li
            v-for="(option, index) in shown"
            :id="optionId(index)"
            :key="String(option.value)"
            role="option"
            class="eqsel__option"
            :class="{
              'eqsel__option--active': index === active,
              'eqsel__option--selected': option.value === model,
            }"
            :aria-selected="option.value === model"
            @mouseenter="active = index"
            @mousedown.prevent
            @click="choose(option)"
          >
            <i v-if="option.icon" class="pi" :class="option.icon" />
            <span class="eqsel__text">
              <span class="eqsel__label">{{ option.label }}</span>
              <span v-if="option.description" class="eqsel__desc">
                {{ option.description }}
              </span>
            </span>
            <i v-if="option.value === model" class="pi pi-check eqsel__check" />
          </li>
          <li v-if="!shown.length" class="eqsel__none">—</li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.eqsel {
  position: relative;
  min-width: 0;
}
.eqsel__trigger {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  height: 2.5rem;
  padding: 0 0.75rem;
  box-sizing: border-box;
  border: 1px solid var(--nxt-outline);
  border-radius: 0.75rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.eqsel__trigger:hover:not(:disabled) {
  border-color: var(--nxt-on-surface-muted);
}
.eqsel__trigger:focus-visible,
.eqsel--open .eqsel__trigger {
  border-color: var(--nxt-on-surface);
  outline: none;
  box-shadow: 0 0 0 3px var(--nxt-outline-variant);
}
.eqsel__trigger:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.eqsel__trigger--placeholder .eqsel__value {
  color: var(--nxt-on-surface-muted);
}
.eqsel__lead {
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
}
.eqsel__value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.eqsel__chevron {
  font-size: 0.7rem;
  color: var(--nxt-on-surface-muted);
  transition: transform 0.15s;
}
.eqsel--open .eqsel__chevron {
  transform: rotate(180deg);
}
</style>

<style>
/* Hors de la portée du composant : le menu est téléporté dans <body>, au-dessus des modales (z-index 1000). */
.eqsel__panel {
  position: fixed;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.9rem;
  background: var(--nxt-card-bg);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
  animation: eqsel-in 0.12s ease-out;
}
.eqsel__panel--up {
  transform: translateY(-100%);
}
@keyframes eqsel-in {
  from {
    opacity: 0;
  }
}
.eqsel__search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
  color: var(--nxt-on-surface-muted);
}
.eqsel__search input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  outline: none;
}
.eqsel__list {
  margin: 0;
  padding: 0.35rem;
  overflow-y: auto;
  list-style: none;
}
.eqsel__option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.65rem;
  border-radius: 0.6rem;
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  cursor: pointer;
}
.eqsel__option--active {
  background: var(--nxt-surface-high);
}
.eqsel__option--selected .eqsel__label {
  font-weight: 700;
}
.eqsel__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}
.eqsel__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.eqsel__desc {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.eqsel__check {
  font-size: 0.75rem;
}
.eqsel__none {
  padding: 0.75rem;
  text-align: center;
  color: var(--nxt-on-surface-muted);
}
</style>
